/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import { Form, Row } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../_augmentt/selectors/customers'
import { ModalPanel, ModalSidebar } from '../../../components/ModalSidebar'
import { getEmployeeFullName } from '../../../util/table.util'
import { runBlockSignInTask } from './blockSignInTask'
import { MODALS } from './IntegrationModals/useIntegrationModals'

/**
 * Modal for Block and Unblock sign in operations
 */
function UnblockSignInModal(props) {
  const { show, onHide, onUpdate, employees, modalType, selectedEmployee, onBack, size = 'md', onSubmitRequest } = props
  const unblock = modalType === MODALS.UNBLOCK_SIGNIN

  const employeesToUpdate =
    !!selectedEmployee && // check that selectedEmployee is not null,
    !employees.includes(selectedEmployee) // and then check if it is not in the list of employees
      ? [selectedEmployee]
      : []

  const employeesNoModify = []
  ;(employees || []).forEach((emp) => {
    if ((unblock && !emp.enabled) || (!unblock && emp.enabled)) {
      employeesToUpdate.push(emp)
    } else {
      employeesNoModify.push(emp)
    }
  })

  const intl = useIntl()

  // Global state
  const currentUser = useSelector(({ user }) => user.data)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)
  const selectedCustomer = useSelector(getSelectedCustomer)

  // Local state
  const [isLoading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  const [status, setStatus] = useState()

  const hide = () => {
    onHide()
    setStatus()
    setErrorMessage(null)
  }

  const unblockSignIn = async () => {
    const accountEnabled = unblock
    const userIds = employeesToUpdate?.map((emp) => emp.employeeIdentifier)
    setLoading(true)
    setErrorMessage(null)

    try {
      await runBlockSignInTask({
        accountEnabled,
        customerId: selectedCustomer?.id,
        organizationId: selectedCustomer?.organization,
        userId: currentUser?.id,
        userIds,
      })

      setStatus('Success')
      await onUpdate?.()
    } catch (err) {
      console.error(`Failed to update account status to ${accountEnabled} for users ${userIds}`, err)
      const message =
        err?.response?.data?.message || // case request fails in backend
        err?.message // if error is thrown in a task
      setErrorMessage(message)
    }

    setLoading(false)
  }

  const handleSubmit = () => {
    if (onSubmitRequest) {
      onSubmitRequest({ employeesToUpdate, unblock })
      return
    }

    unblockSignIn()
  }

  const title = unblock ? 'ENGAGE.UNBLOCK_SIGNIN' : 'ENGAGE.BLOCK_SIGNIN'
  const subtitle =
    selectedIntegration?.IntegrationApplication?.display_name === 'M365'
      ? 'Microsoft 365'
      : selectedIntegration?.IntegrationApplication?.display_name

  return (
    <ModalSidebar
      show={show}
      onHide={hide}
      size={size}
      title={title}
      subTitles={subtitle ? [subtitle] : []}
      isLoading={isLoading}
      headerBackButtonAction={onBack}
    >
      <ModalPanel
        leftButton={status ? 'close' : { label: 'GENERAL.CLOSE', onClick: hide, variant: 'light' }}
        rightButton={
          !status
            ? {
                label: title,
                onClick: handleSubmit,
                variant: 'secondary',
                className: 'bg-color-engage',
                id: unblock ? 'unblock-signin-submit-btn' : 'block-signin-submit-btn',
              }
            : null
        }
      >
        <Form>
          {!isLoading && !status && (
            <Row>
              <div className="col-sm-12 col-md-12 col-lg-12">
                <p>
                  <FormattedMessage id="ENGAGE.BLOCK_SIGNIN_DESC" />
                </p>
                <p>
                  <FormattedMessage id="ENGAGE.BLOCK_SIGNIN_DESC_2" />
                </p>
                <div>
                  <Form.Group id="formGridCheckbox">
                    <Form.Check
                      custom
                      type="checkbox"
                      id="confirmBlock"
                      checked={!unblock}
                      disabled
                      label={intl.formatMessage({
                        id: 'ENGAGE.BLOCK_USER_FROM_SIGNING_IN',
                      })}
                    />
                    {unblock && (
                      <span className="font-color-2" style={{ marginLeft: '35px' }}>
                        <FormattedMessage id="ENGAGE.UNBLOCK_DISCLAIMER" />
                      </span>
                    )}
                  </Form.Group>
                </div>
                <p className="font-bolder mb-1 mt-4">
                  <FormattedMessage id="ENGAGE.UNBLOCK_SIGNIN_FOR_EMPLOYEES" values={{ unblock }} />
                </p>
                <ul className="font-bolder">
                  {employeesToUpdate?.map((employee) => (
                    <li key={employee.employeeIdentifier || employee.id || employee.email}>
                      {getEmployeeFullName(employee)}
                    </li>
                  ))}
                </ul>
                {employeesNoModify?.length ? (
                  <>
                    <p className="mb-1 mt-4">
                      <FormattedMessage
                        id="ENGAGE.UNMODIFIED_EMPLOYEES"
                        values={{ unblock, num: employeesNoModify?.length }}
                      />
                    </p>
                    <ul>
                      {employeesNoModify?.map((employee) => (
                        <li key={employee.employeeIdentifier || employee.id || employee.email}>
                          {getEmployeeFullName(employee)}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            </Row>
          )}
          {!isLoading && errorMessage && (
            <Row>
              <div className="col-sm-12 col-md-12 col-lg-12">
                <span className="icon-lg font-color-danger fas fa-circle-xmark" />{' '}
                <span className="ms-3">
                  <FormattedMessage id={title} />
                </span>
                <p className="font-color-danger" style={{ marginLeft: '2.5rem' }}>
                  {errorMessage ? String(errorMessage) : intl.formatMessage({ id: 'GENERAL.ERROR' })}
                </p>
              </div>
            </Row>
          )}
          {!isLoading && !errorMessage && status && (
            <Row>
              <div className="col-sm-12 col-md-12 col-lg-12">
                <span className="icon-lg font-color-engage fas fa-circle-check" />{' '}
                <span className="ms-3">
                  <FormattedMessage id={title} />
                </span>
              </div>
            </Row>
          )}
          {isLoading && (
            <Row>
              <div className="col-sm-12 col-md-12 col-lg-12">
                <div className="centered">
                  <p className="font-lg font-bolder font-color-2">
                    <FormattedMessage id="ENGAGE.UNBLOCKING_SIGN_IN" values={{ unblock }} />
                  </p>
                </div>
              </div>
            </Row>
          )}
        </Form>
      </ModalPanel>
    </ModalSidebar>
  )
}

export default UnblockSignInModal
